import React, { useEffect, useRef, useState } from 'react';
import { Download, Eye, MoreHorizontal, Trash2, Link as LinkIcon } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { libraryItemKey } from '../../api/images.api';

export const ImageCard = ({ item, onView, onDownload, onDelete, onCopyLink }) => {
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const key = libraryItemKey(item);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onPointerDown = (event) => {
      if (!menuRef.current?.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [menuOpen]);

  return (
    <article className="group relative overflow-hidden rounded-2xl bg-neutral-100 dark:bg-neutral-900">
      <button
        type="button"
        onClick={() => onView?.(item)}
        className="block w-full text-start"
        aria-label={item.prompt ? `${t('imageView')}: ${item.prompt}` : t('imageView')}
      >
        <img
          src={item.url}
          alt={item.prompt || t('imageSection')}
          className="aspect-square w-full object-cover"
          loading="lazy"
        />
      </button>
      {item.prompt ? (
        <p className="pointer-events-none absolute inset-x-0 bottom-0 line-clamp-2 bg-gradient-to-t from-black/70 to-transparent px-3 pb-2.5 pt-8 text-xs text-white opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100">
          {item.prompt}
        </p>
      ) : null}
      <div className="absolute inset-x-2 top-2 flex justify-end gap-1 opacity-100 transition sm:opacity-0 sm:group-hover:opacity-100 sm:group-focus-within:opacity-100">
        <button
          type="button"
          onClick={() => onView?.(item)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white"
          aria-label={t('imageView')}
        >
          <Eye size={14} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => onDownload?.(item)}
          className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white"
          aria-label={t('imageDownload')}
        >
          <Download size={14} aria-hidden />
        </button>
        <div className="relative" ref={menuRef}>
          <button
            type="button"
            onClick={() => setMenuOpen((open) => !open)}
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-black/65 text-white"
            aria-label={t('imageMore')}
            aria-expanded={menuOpen}
          >
            <MoreHorizontal size={14} aria-hidden />
          </button>
          {menuOpen ? (
            <div className="absolute end-0 z-20 mt-1 min-w-[11rem] overflow-hidden rounded-xl border border-neutral-200 bg-white py-1 shadow-lg dark:border-neutral-800 dark:bg-neutral-950">
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-neutral-700 hover:bg-neutral-50 dark:text-neutral-200 dark:hover:bg-neutral-900"
                onClick={() => {
                  setMenuOpen(false);
                  onCopyLink?.(item);
                }}
              >
                <LinkIcon size={14} aria-hidden />
                {t('imageCopyLink')}
              </button>
              <button
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-start text-sm text-rose-600 hover:bg-rose-50 dark:text-rose-300 dark:hover:bg-rose-950/40"
                onClick={() => {
                  setMenuOpen(false);
                  onDelete?.(item);
                }}
              >
                <Trash2 size={14} aria-hidden />
                {t('imageDelete')}
              </button>
            </div>
          ) : null}
        </div>
      </div>
      <span className="sr-only">{key}</span>
    </article>
  );
};
